import { TriageSuggestionStatusEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { iconsByTypename } from "@/objects/@icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type SuggestionIndicatorSummary = {
  hasDocument: boolean;
  hasTransaction: boolean;
  showAccount: boolean;
  missingDocumentLineItems: boolean;
};

type SuggestionWithLines = {
  identifier?: string;
  status?: TriageSuggestionStatusEnum | null;
  lines?: Array<{ id?: string; identifier?: string }> | null;
  ruleSuggestionDocumentOptions?: Array<unknown> | null;
  ruleSuggestionTransactionOptions?: Array<unknown> | null;
};

export function matchSuggestionIndicatorSummary(
  suggestion: SuggestionWithLines,
): SuggestionIndicatorSummary | null {
  if (suggestion.status !== TriageSuggestionStatusEnum.Pending) {
    return null;
  }

  if (!Array.isArray(suggestion.lines) || suggestion.lines.length === 0) {
    return null;
  }

  const hasDocument =
    (suggestion.ruleSuggestionDocumentOptions?.length ?? 0) > 0;
  const hasTransaction =
    (suggestion.ruleSuggestionTransactionOptions?.length ?? 0) > 0;

  return {
    hasDocument,
    hasTransaction,
    showAccount: !hasDocument && !hasTransaction,
    missingDocumentLineItems: hasDocument && !hasTransaction,
  };
}

export function aggregateSuggestionIndicatorSummaries(
  summaries: SuggestionIndicatorSummary[],
): SuggestionIndicatorSummary | null {
  if (summaries.length === 0) {
    return null;
  }

  const hasDocument = summaries.some((summary) => summary.hasDocument);
  const hasTransaction = summaries.some((summary) => summary.hasTransaction);

  return {
    hasDocument,
    hasTransaction,
    showAccount:
      summaries.some((summary) => summary.showAccount) &&
      !hasDocument &&
      !hasTransaction,
    missingDocumentLineItems: hasDocument && !hasTransaction,
  };
}

function suggestionIndicatorTooltip(
  summary: SuggestionIndicatorSummary,
): string {
  const parts: string[] = [];
  if (summary.hasDocument) {
    parts.push("Linked document");
  }
  if (summary.hasTransaction) {
    parts.push("Linked transaction");
  }
  if (summary.missingDocumentLineItems) {
    parts.push("Document is missing line items");
  }
  if (summary.showAccount) {
    parts.push("Journal entry proposal");
  }
  return parts.join(" · ");
}

export function TriageSuggestionIndicatorPill({
  summary,
  "data-testid": dataTestId = "triage-suggestion-indicator",
}: {
  summary: SuggestionIndicatorSummary;
  "data-testid"?: string;
}) {
  const icons: Array<{
    typename: keyof typeof iconsByTypename;
    label: string;
  }> = [];
  if (summary.hasDocument) {
    icons.push({ typename: "DocumentObject", label: "Document" });
  }
  if (summary.hasTransaction) {
    icons.push({ typename: "TransactionObject", label: "Transaction" });
  }
  if (summary.showAccount) {
    icons.push({ typename: "ChartedAccountObject", label: "Account" });
  }

  if (icons.length === 0) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          data-testid={dataTestId}
          className="inline-flex items-center gap-0.5 pl-1 pr-1 pt-px pb-px rounded-sm bg-accent [border:1px_solid_var(--border)]"
        >
          {icons.map((icon) => (
            <Icon
              key={icon.typename}
              ReactIcon={iconsByTypename[icon.typename]}
              size="xs"
              aria-label={icon.label}
            />
          ))}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">
        {suggestionIndicatorTooltip(summary)}
      </TooltipContent>
    </Tooltip>
  );
}

export function TriageSuggestionIndicators({
  suggestions,
}: {
  suggestions: SuggestionWithLines[] | null | undefined;
}) {
  const summaries = (suggestions ?? [])
    .map(matchSuggestionIndicatorSummary)
    .filter(
      (summary): summary is SuggestionIndicatorSummary => summary != null,
    );
  const aggregate = aggregateSuggestionIndicatorSummaries(summaries);

  if (aggregate == null) {
    return null;
  }

  return (
    <div
      data-testid="triage-row-suggestion-indicators"
      className="inline-flex items-center shrink-0"
    >
      <TriageSuggestionIndicatorPill
        data-testid="triage-row-suggestion-indicator"
        summary={aggregate}
      />
    </div>
  );
}
